/**
 * QA Session Utilities
 *
 * Helper functions for the QA session system including screenshot capture,
 * report generation, event formatting, and storage upload.
 */

import { supabase } from '@/lib/supabase'
import type { QASession, QAReport, QAEvent, QARiskLevel } from '@/types/qa-session'

/**
 * Capture a screenshot of the current page, excluding the QA toolbar
 * Uses html2canvas for client-side capture
 */
export async function captureScreenshot(excludeToolbar: boolean = true): Promise<string> {
  try {
    // Find the QA toolbar element to exclude
    const toolbarElement = excludeToolbar
      ? (document.querySelector('[data-qa-toolbar="true"]') as HTMLElement)
      : null

    // Temporarily hide the toolbar
    const originalDisplay = toolbarElement?.style.display || ''
    if (toolbarElement) {
      toolbarElement.style.display = 'none'
    }

    // Small delay to ensure toolbar is hidden before capture
    await new Promise((resolve) => setTimeout(resolve, 100))

    // Load html2canvas only when a screenshot is actually requested
    const { default: html2canvas } = await import('html2canvas')

    // Capture the screenshot
    const canvas = await html2canvas(document.body, {
      allowTaint: true,
      useCORS: true,
      logging: false,
      scale: 0.5, // Reduce size for performance
      ignoreElements: (element) => {
        // Double-check: ignore any element with qa-toolbar data attribute
        return element.hasAttribute('data-qa-toolbar')
      },
    })

    // Always restore toolbar visibility
    if (toolbarElement) {
      toolbarElement.style.display = originalDisplay
    }

    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('Failed to capture screenshot:', error)
    // Ensure toolbar is restored even on error
    const toolbarElement = document.querySelector('[data-qa-toolbar="true"]') as HTMLElement
    if (toolbarElement) {
      toolbarElement.style.display = ''
    }
    throw error
  }
}

/**
 * Upload a screenshot to Supabase Storage
 * Returns the storage path
 */
export async function uploadScreenshot(
  sessionId: string,
  screenshotId: string,
  dataUrl: string,
  description?: string,
): Promise<string> {
  try {
    // Convert data URL to blob
    const response = await fetch(dataUrl)
    const blob = await response.blob()

    // Generate file path: screenshots/{session_id}/{timestamp}_{description}.png
    const timestamp = Date.now()
    const sanitizedDesc = description
      ? description.toLowerCase().replace(/[^a-z0-9]/g, '_')
      : 'screenshot'
    const filename = `${timestamp}_${sanitizedDesc}.png`
    const filePath = `screenshots/${sessionId}/${filename}`

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage.from('qa-testing').upload(filePath, blob, {
      contentType: 'image/png',
      upsert: false,
    })

    if (error) {
      console.error('Failed to upload screenshot:', error)
      throw error
    }

    return data.path
  } catch (error) {
    console.error('Screenshot upload error:', error)
    throw error
  }
}

/**
 * Get a signed URL for a screenshot in storage
 */
export async function getScreenshotUrl(
  storagePath: string,
  expiresIn: number = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('qa-testing')
    .createSignedUrl(storagePath, expiresIn)

  if (error) {
    console.error('Failed to get screenshot URL:', error)
    throw error
  }

  return data.signedUrl
}

/**
 * Start screen recording using MediaRecorder API
 * Returns a MediaRecorder instance and a promise that resolves with the blob
 */
export async function startScreenRecording(): Promise<{
  recorder: MediaRecorder
  stopRecording: () => Promise<Blob>
}> {
  try {
    // Request screen capture with audio
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      } as MediaTrackConstraints,
      audio: false, // Set to true if you want to capture audio
    })

    // Create MediaRecorder with webm format
    const mimeType = MediaRecorder.isTypeSupported('video/webm; codecs=vp9')
      ? 'video/webm; codecs=vp9'
      : 'video/webm'

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 2500000, // 2.5 Mbps
    })

    const chunks: Blob[] = []

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data)
      }
    }

    // Start recording
    recorder.start(1000) // Capture in 1-second chunks

    // Return recorder and stop function
    const stopRecording = (): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType })
          // Stop all tracks to end screen sharing
          stream.getTracks().forEach((track) => track.stop())
          resolve(blob)
        }

        recorder.onerror = (error) => {
          stream.getTracks().forEach((track) => track.stop())
          reject(error)
        }

        if (recorder.state !== 'inactive') {
          recorder.stop()
        }
      })
    }

    return { recorder, stopRecording }
  } catch (error) {
    console.error('Failed to start screen recording:', error)
    throw error
  }
}

/**
 * Upload a screen recording to Supabase Storage
 * Returns the storage path
 */
export async function uploadRecording(
  sessionId: string,
  recordingId: string,
  blob: Blob,
  description?: string,
): Promise<string> {
  try {
    // Generate file path: screen-recordings/{session_id}/{timestamp}_{description}.webm
    const timestamp = Date.now()
    const sanitizedDesc = description
      ? description.toLowerCase().replace(/[^a-z0-9]/g, '_')
      : 'recording'
    const filename = `${timestamp}_${sanitizedDesc}.webm`
    const filePath = `screen-recordings/${sessionId}/${filename}`

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage.from('qa-testing').upload(filePath, blob, {
      contentType: blob.type || 'video/webm',
      upsert: false,
    })

    if (error) {
      console.error('Failed to upload recording:', error)
      throw error
    }

    return data.path
  } catch (error) {
    console.error('Recording upload error:', error)
    throw error
  }
}

/**
 * Get a signed URL for a recording in storage
 */
export async function getRecordingUrl(
  storagePath: string,
  expiresIn: number = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('qa-testing')
    .createSignedUrl(storagePath, expiresIn)

  if (error) {
    console.error('Failed to get recording URL:', error)
    throw error
  }

  return data.signedUrl
}

/**
 * Generate a unique ID for QA entities
 */
export function generateQAId(prefix: string = 'qa'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Get current route from window location
 */
export function getCurrentRoute(): string {
  return window.location.pathname + window.location.search
}

/**
 * Format timestamp to readable string
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  })
}

/**
 * Format duration in milliseconds to readable string
 */
export function formatDuration(ms: number): string {
  // A negative elapsed time is a clock skew artifact, not a duration. Without
  // the clamp `Math.floor(-1000 / 1000)` is -1 and the function renders "-1s".
  const seconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Get risk level color for UI display
 */
export function getRiskLevelColor(level: QARiskLevel): string {
  switch (level) {
    case 'R0':
      return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
    case 'R1':
      return 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
    case 'R2':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
    case 'R3':
      return 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
    // The switch is exhaustive over QARiskLevel, so this is unreachable by the
    // type. It exists because the value often arrives from persisted session
    // JSON rather than from a typed construction, and returning `undefined`
    // into a className produced a literally unstyled badge.
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-800'
  }
}

/**
 * Get risk level label
 */
export function getRiskLevelLabel(level: QARiskLevel): string {
  switch (level) {
    case 'R0':
      return 'Critical'
    case 'R1':
      return 'High'
    case 'R2':
      return 'Medium'
    case 'R3':
      return 'Low'
    // Same reasoning as getRiskLevelColor: unreachable by the type, reachable
    // from persisted data. "Unknown" is honest; `undefined` rendered as a blank
    // cell that read as "no risk".
    default:
      return 'Unknown'
  }
}

/**
 * Generate JSON report from QA session
 */
export function generateJSONReport(session: QASession): QAReport {
  return {
    session,
    generatedAt: Date.now(),
    format: 'json',
    content: JSON.stringify(session, null, 2),
  }
}

/**
 * Generate Markdown report from QA session
 */
export function generateMarkdownReport(session: QASession): QAReport {
  const { metadata, events, screenshots, routesVisited, summary } = session

  let markdown = `# QA Session Report\n\n`
  markdown += `**Session ID:** ${metadata.sessionId}\n\n`
  markdown += `**Started:** ${formatTimestamp(metadata.startTime)}\n\n`

  if (metadata.endTime) {
    markdown += `**Ended:** ${formatTimestamp(metadata.endTime)}\n\n`
    markdown += `**Duration:** ${formatDuration(metadata.duration || 0)}\n\n`
  }

  markdown += `**User Type:** ${metadata.userType}\n\n`
  markdown += `**Initial Route:** ${metadata.initialRoute}\n\n`
  markdown += `**Viewport:** ${metadata.viewport.width}x${metadata.viewport.height}\n\n`

  markdown += `## Summary\n\n`
  markdown += `- **Total Events:** ${summary.totalEvents}\n`
  markdown += `- **Console Errors:** ${summary.consoleErrors}\n`
  markdown += `- **Network Errors:** ${summary.networkErrors}\n`
  markdown += `- **Permission Violations:** ${summary.permissionViolations}\n`
  markdown += `- **Manual Flags:** ${summary.manualFlags}\n`
  markdown += `- **Screenshots:** ${screenshots.length}\n\n`

  markdown += `### Errors by Risk Level\n\n`
  markdown += `- **R0 (Critical):** ${summary.errorsByRisk.R0}\n`
  markdown += `- **R1 (High):** ${summary.errorsByRisk.R1}\n`
  markdown += `- **R2 (Medium):** ${summary.errorsByRisk.R2}\n`
  markdown += `- **R3 (Low):** ${summary.errorsByRisk.R3}\n\n`

  markdown += `### Routes Visited\n\n`
  routesVisited.forEach((route) => {
    markdown += `- ${route}\n`
  })
  markdown += `\n`

  markdown += `## Events Timeline\n\n`
  events.forEach((event, index) => {
    markdown += `### Event ${index + 1}: ${event.type}\n\n`
    markdown += `- **Time:** ${formatTimestamp(event.timestamp)}\n`
    markdown += `- **Risk Level:** ${getRiskLevelLabel(event.riskLevel)}\n`
    markdown += `- **Route:** ${event.route}\n`
    markdown += `- **Message:** ${event.message}\n`

    if (event.flagged) {
      markdown += `- **⚠️ FLAGGED**\n`
    }

    if (event.details) {
      markdown += `- **Details:**\n`
      Object.entries(event.details).forEach(([key, value]) => {
        markdown += `  - ${key}: ${value}\n`
      })
    }

    markdown += `\n`
  })

  markdown += `## Screenshots\n\n`
  screenshots.forEach((screenshot, index) => {
    markdown += `### Screenshot ${index + 1}\n\n`
    markdown += `- **Time:** ${formatTimestamp(screenshot.timestamp)}\n`
    markdown += `- **Route:** ${screenshot.route}\n`
    markdown += `- **Triggered By:** ${screenshot.triggeredBy}\n\n`
  })

  return {
    session,
    generatedAt: Date.now(),
    format: 'markdown',
    content: markdown,
  }
}

/**
 * Download report as file
 */
export function downloadReport(report: QAReport, filename?: string): void {
  const ext = report.format === 'json' ? 'json' : 'md'
  const name = filename || `qa-session-${report.session.metadata.sessionId}.${ext}`
  const blob = new Blob([report.content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Save QA session to localStorage
 */
export function saveSessionToStorage(session: QASession): void {
  try {
    const stored = localStorage.getItem('qa_sessions')
    const sessions = stored ? JSON.parse(stored) : []
    sessions.push({
      ...session,
      id: session.metadata.sessionId,
      createdAt: Date.now(),
    })
    // Keep only last 50 sessions
    const trimmed = sessions.slice(-50)
    localStorage.setItem('qa_sessions', JSON.stringify(trimmed))
  } catch (error) {
    console.error('Failed to save QA session to storage:', error)
  }
}

/**
 * Load all QA sessions from localStorage
 */
export function loadSessionsFromStorage(): QASession[] {
  try {
    const stored = localStorage.getItem('qa_sessions')
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to load QA sessions from storage:', error)
    return []
  }
}

/**
 * Clear all QA sessions from localStorage
 */
export function clearSessionStorage(): void {
  try {
    localStorage.removeItem('qa_sessions')
  } catch (error) {
    console.error('Failed to clear QA session storage:', error)
  }
}

/**
 * Truncate long strings for display
 */
export function truncate(str: string, maxLength: number = 100): string {
  if (str.length <= maxLength) return str
  // trimEnd so a cut landing on a space does not render as "word ..."
  return str.substring(0, maxLength).trimEnd() + '...'
}

/**
 * Extract error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'Unknown error'
}

/**
 * Extract stack trace from error
 */
export function extractStackTrace(error: unknown): string | undefined {
  if (error instanceof Error && error.stack) {
    return error.stack
  }
  return undefined
}
